import SaveIcon from '@mui/icons-material/Save';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import CardMedia from '@mui/material/CardMedia';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { useSnackbar } from 'notistack';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import DashboardContentPageLayout from 'src/common/components/dashboard/DashboardContentPageLayout';
import { dashboardPrimaryActionButtonProps } from 'src/common/components/DashboardPageToolbar';
import MediaManagerModal from 'src/common/components/modals/MediaManagerModal';
import { frostedPanelSx } from 'src/common/styles/frostedPanelStyles';
import { bakeSocialSharePreview } from 'src/helpers/bakeSocialSharePreview';
import { getAxiosErrorMessage } from 'src/helpers/axiosError';
import { syncSocialShareFileUsage } from 'src/helpers/socialShareFileUsage';
import {
  SOCIAL_SHARE_OG_HEIGHT,
  SOCIAL_SHARE_OG_WIDTH,
} from 'src/helpers/socialShareConstants';
import useDeferredFileUsage from 'src/hooks/useDeferredFileUsage';
import { LanguageKeys } from 'src/common/constants/languages';
import { UploadedFile } from 'src/interfaces/FileManager';
import { SocialShareImageMode, SocialShareSettings } from 'src/interfaces/SocialShare';
import { useFileUploadBatch } from 'src/pages/Dashboard/System/FileManager/components/useFileUploadBatch';
import useFileManagerStore from 'src/stores/fileManagerStore';
import useSocialShareStore, { emptySocialShareSettings } from 'src/stores/socialShareStore';
import useTrackMapsStore from 'src/stores/trackMapsStore';
import SocialShareComposer from './SocialShareComposer';
import SocialSharePreviewCards from './SocialSharePreviewCards';
import SocialShareTextFields from './SocialShareTextFields';

const panelSx = {
  ...frostedPanelSx(0.5),
  p: { xs: 2, sm: 3 },
  mb: 2,
};

export default function SocialSharingPage() {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { settings, isLoading, fetchSettings, saveSettings } = useSocialShareStore(s => s);
  const trackMaps = useTrackMapsStore(s => s.trackMaps);
  const { uploadToEndpoint } = useFileUploadBatch();
  const resolveFileIdsByUrls = useFileManagerStore(s => s.resolveFileIdsByUrls);
  const markFileUsage = useFileManagerStore(s => s.markFileUsage);
  const markPendingFilesForContent = useFileManagerStore(s => s.markPendingFilesForContent);

  const [form, setForm] = useState<SocialShareSettings>(emptySocialShareSettings());
  const [editorLang, setEditorLang] = useState<LanguageKeys>(LanguageKeys.hu);
  const [mediaOpen, setMediaOpen] = useState(false);
  const [saveOverlay, setSaveOverlay] = useState<'generating' | 'saving' | null>(null);
  const isSaving = saveOverlay !== null;
  const previewRef = useRef<HTMLDivElement | null>(null);

  const { addPendingFile } = useDeferredFileUsage({
    contentType: 'socialShare',
    contentId: 'site-default',
    fieldName: 'shareImage',
  });

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    if (settings) {
      setForm(settings);
    }
  }, [settings]);

  const imageMode = form.imageMode;
  const isComposed = imageMode === 'composed';

  const handleImageSelect = (file: UploadedFile) => {
    if (file.uploadEndpoint !== 'image') return;
    setForm(prev => ({
      ...prev,
      imageFileId: file.id,
      imageUrl: file.fileUrl,
      imageWidth: SOCIAL_SHARE_OG_WIDTH,
      imageHeight: SOCIAL_SHARE_OG_HEIGHT,
    }));
    addPendingFile(file.id);
    setMediaOpen(false);
  };

  const handleSave = async () => {
    setSaveOverlay(isComposed ? 'generating' : 'saving');
    try {
      let payload = { ...form };

      if (isComposed && previewRef.current) {
        const file = await bakeSocialSharePreview(previewRef.current);
        const results = await uploadToEndpoint([file], 'image', { compress: false });
        const uploaded = results[0];
        if (uploaded) {
          const fileIdsByUrl = await resolveFileIdsByUrls([uploaded.url]);
          const imageFileId = fileIdsByUrl.get(uploaded.url);
          payload = {
            ...payload,
            imageUrl: uploaded.url,
            imageFileId,
            imageWidth: SOCIAL_SHARE_OG_WIDTH,
            imageHeight: SOCIAL_SHARE_OG_HEIGHT,
          };
          if (imageFileId) {
            addPendingFile(imageFileId);
          }
        }
        setSaveOverlay('saving');
      }

      const previousSettings = settings ?? emptySocialShareSettings();
      const saved = await saveSettings(payload);
      await markPendingFilesForContent('socialShare', 'site-default');
      await syncSocialShareFileUsage(markFileUsage, previousSettings, saved);
      enqueueSnackbar(t('system.socialSharing.saveSuccess'), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(getAxiosErrorMessage(error, t('system.socialSharing.errors.fallback')), {
        variant: 'error',
      });
    } finally {
      setSaveOverlay(null);
    }
  };

  const previewLocale = form.locales[editorLang] ?? form.locales.hu;

  return (
    <DashboardContentPageLayout
      title={t('system.socialSharing.pageTitle')}
      isLoading={isLoading && !settings}
      headerActions={
        <Button
          {...dashboardPrimaryActionButtonProps()}
          startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={isSaving || isLoading}
        >
          {t('base.save')}
        </Button>
      }
    >
      <Box sx={{ position: 'relative' }}>
        <Backdrop
          open={isSaving}
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: theme => theme.zIndex.drawer + 1,
            flexDirection: 'column',
            gap: 2,
            backdropFilter: 'blur(3px)',
          }}
        >
          <CircularProgress color="inherit" />
          <Typography variant="body1" color="inherit">
            {saveOverlay === 'generating'
              ? t('system.socialSharing.generatingImage')
              : t('system.socialSharing.saving')}
          </Typography>
        </Backdrop>

        <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <Paper variant="outlined" sx={panelSx}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              {t('system.socialSharing.textSectionTitle')}
            </Typography>
            <SocialShareTextFields
              locales={form.locales}
              editorLang={editorLang}
              onEditorLangChange={setEditorLang}
              onLocalesChange={locales => setForm(prev => ({ ...prev, locales }))}
            />
          </Paper>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Paper variant="outlined" sx={panelSx}>
            <FormControlLabel
              control={
                <Switch
                  checked={isComposed}
                  onChange={(_, checked) =>
                    setForm(prev => ({
                      ...prev,
                      imageMode: (checked ? 'composed' : 'upload') as SocialShareImageMode,
                    }))
                  }
                />
              }
              label={
                isComposed
                  ? t('system.socialSharing.imageModeComposed')
                  : t('system.socialSharing.imageModeUpload')
              }
            />

            {!isComposed ? (
              <Box sx={{ mt: 2 }}>
                {form.imageUrl ? (
                  <CardMedia
                    component="img"
                    src={form.imageUrl}
                    sx={{
                      maxHeight: 200,
                      objectFit: 'contain',
                      mb: 2,
                      borderRadius: 1,
                    }}
                  />
                ) : null}
                <Button variant="outlined" onClick={() => setMediaOpen(true)}>
                  {t('system.socialSharing.selectImage')}
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  {t('system.socialSharing.imageSizeHint', {
                    width: SOCIAL_SHARE_OG_WIDTH,
                    height: SOCIAL_SHARE_OG_HEIGHT,
                  })}
                </Typography>
              </Box>
            ) : null}
          </Paper>
        </Grid>

        {isComposed ? (
          <Grid size={{ xs: 12 }}>
            <SocialShareComposer
              key={settings?.updatedAt ?? 'initial'}
              composedDocument={form.composedDocument}
              backgroundColor={form.backgroundColor}
              backgroundSettings={{
                backgroundImageUrl: form.backgroundImageUrl,
                backgroundImageFileId: form.backgroundImageFileId,
                backgroundAlignH: form.backgroundAlignH,
                backgroundAlignV: form.backgroundAlignV,
              }}
              previewLang={editorLang}
              previewRef={previewRef}
              trackMaps={trackMaps}
              onDocumentChange={composedDocument =>
                setForm(prev => ({ ...prev, composedDocument }))
              }
              onBackgroundChange={settings =>
                setForm(prev => ({
                  ...prev,
                  backgroundImageUrl: settings.backgroundImageUrl,
                  backgroundImageFileId: settings.backgroundImageFileId,
                  backgroundAlignH: settings.backgroundAlignH,
                  backgroundAlignV: settings.backgroundAlignV,
                }))
              }
            />
          </Grid>
        ) : null}

        <Grid size={{ xs: 12 }}>
          <Paper variant="outlined" sx={{ ...panelSx, mb: 0 }}>
            <SocialSharePreviewCards
              locale={previewLocale}
              imageUrl={form.imageUrl}
              siteName={form.siteName}
              previewLang={editorLang}
            />
          </Paper>
        </Grid>
        </Grid>
      </Box>

      <MediaManagerModal
        open={mediaOpen}
        onClose={() => setMediaOpen(false)}
        onSelect={handleImageSelect}
        endpoint="image"
        title={t('system.socialSharing.selectImage')}
      />
    </DashboardContentPageLayout>
  );
}
